<script setup>
import { computed } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { formatDuration, formatTimeValue, useReportFormatter } from '@/composables/report'
import { useNavigator } from '@/composables/navigator'
import { useAccountingStore } from '@/stores/accounting.store'

import KeyValueList from '@/components/shared/KeyValueList.vue'
import ListItem from '@/components/shared/ListItem.vue'

const props = defineProps({
  row: {
    type: Object,
    required: true,
  },
  section: {
    type: Object,
    required: true,
  },
  build: {
    type: Object,
    required: true,
  },
  sourceRows: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['cancel'])

const i18n = getI18n()
const accountingStore = useAccountingStore()
const formatter = useReportFormatter()
const { reportColumnKindEnum, reportRowKindEnum } = accountingStore
useNavigator(() => emit('cancel'))

const TEXT_BLOCK_KEYS = Object.freeze(['comments', 'notes'])

const items = computed(() => props.section.columns
  .filter(column => !TEXT_BLOCK_KEYS.includes(column.key))
  .map(column => ({
    label: i18n.t(column.label),
    value: column.kind === reportColumnKindEnum.TIME_NOTE ? (props.row.assumed ? i18n.t('common.report.assumedTime') : '') : formatter.cell(column, props.row, props.build),
  })))
const textBlocks = computed(() => props.section.columns
  .filter(column => TEXT_BLOCK_KEYS.includes(column.key) && props.row[column.key])
  .map(column => ({ key: column.key, label: i18n.t(column.label), value: props.row[column.key] })))

const staffRows = computed(() => (props.row.staff || !props.row.date ? [] : props.sourceRows.filter(source => source.kind === reportRowKindEnum.TIME
  && !source.is_excluded && source.date === props.row.date && source.project_id === props.row.project_id)))
</script>

<template>
  <div class="q-px-sm">
    <key-value-list :items="items" />
    <template v-for="block in textBlocks" :key="block.key">
      <q-separator class="q-my-sm" />
      <q-item dense>
        <q-item-section>
          <q-item-label caption>{{ block.label }}</q-item-label>
          <q-item-label class="text-pre-wrap">{{ block.value }}</q-item-label>
        </q-item-section>
      </q-item>
    </template>
    <template v-if="staffRows.length">
      <q-separator class="q-my-sm" />
      <q-list>
        <q-item-label header class="q-pb-xs">{{ i18n.t('common.staff') }}</q-item-label>
        <list-item
          v-for="staffRow in staffRows"
          :key="staffRow.key"
          class="q-px-sm"
          avatar-icon="mdi-account-outline"
          avatar-icon-color="primary"
          :label="staffRow.staff_label"
          :right-label="formatDuration(staffRow.net_minutes)"
          :right-caption="i18n.t('common.report.grossValue', { value: formatDuration(staffRow.gross_minutes) })"
          dense
        >
          <template #caption>
            <div>{{ [staffRow.start ? `${formatTimeValue(staffRow.start)}–${formatTimeValue(staffRow.end)}` : '', staffRow.comments].filter(Boolean).join(' · ') }}</div>
          </template>
        </list-item>
      </q-list>
    </template>
  </div>
</template>
